import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, query, where, orderBy, doc, updateDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Auth functions
export const loginUser = async (email: string, password: string) => {
  return await signInWithEmailAndPassword(auth, email, password);
};

export const registerUser = async (email: string, password: string) => {
  return await createUserWithEmailAndPassword(auth, email, password);
};

export const logoutUser = async () => {
  return await signOut(auth);
};

// Campaign functions
export const saveCampaign = async (userId: string, campaignData: any) => {
  try {
    console.log('Saving campaign data:', campaignData);
    
    const docRef = await addDoc(collection(db, 'campaigns'), {
      ...campaignData,
      userId,
      createdAt: new Date().toISOString(),
      sharing_settings: campaignData.sharing_settings || {
        visibility: 'private',
        view_count: 0
      }
    });
    
    console.log('Campaign saved successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving campaign:', error);
    throw error;
  }
};

// Export with both names for compatibility
export const saveCampaignData = saveCampaign;

export const updateCampaignData = async (campaignId: string, updates: any) => {
  try {
    console.log('Updating campaign with data:', updates);
    
    const campaignRef = doc(db, 'campaigns', campaignId);
    await updateDoc(campaignRef, updates);
    
    console.log('Campaign updated successfully');
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

export const getCampaignData = async (campaignId: string) => {
  try {
    const campaignRef = doc(db, 'campaigns', campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (campaignSnap.exists()) {
      return { id: campaignSnap.id, ...campaignSnap.data() };
    } else {
      throw new Error('Campaign not found');
    }
  } catch (error) {
    console.error('Error getting campaign:', error);
    throw error;
  }
};

export const deleteCampaign = async (campaignId: string) => {
  try {
    console.log('Deleting campaign with ID:', campaignId);
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please log in again.');
    }
    
    console.log('Current user ID:', auth.currentUser.uid);
    
    const campaignRef = doc(db, 'campaigns', campaignId);
    await deleteDoc(campaignRef);
    
    console.log('Campaign deleted successfully');
  } catch (error) {
    console.error('Error deleting campaign:', error);
    
    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('Missing or insufficient permissions')) {
        throw new Error('Permission denied: You can only delete your own campaigns. Please check your Firebase security rules.');
      } else if (error.message.includes('unauthenticated')) {
        throw new Error('Authentication required: Please log in again.');
      }
    }
    
    throw error;
  }
};

export const updateCampaignSharing = async (campaignId: string, sharingSettings: any) => {
  try {
    console.log('Updating campaign sharing settings:', sharingSettings);
    
    const campaignRef = doc(db, 'campaigns', campaignId);
    await updateDoc(campaignRef, { 
      sharing_settings: sharingSettings,
      'sharing_settings.shared_at': new Date().toISOString()
    });
    
    console.log('Campaign sharing settings updated successfully');
  } catch (error) {
    console.error('Error updating campaign sharing:', error);
    throw error;
  }
};

export const getPublicCampaigns = async () => {
  try {
    const q = query(
      collection(db, 'campaigns'),
      where('sharing_settings.visibility', '==', 'public'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting public campaigns:', error);
    throw error;
  }
};

export const getCampaignByShareCode = async (shareCode: string) => {
  try {
    const q = query(
      collection(db, 'campaigns'),
      where('sharing_settings.share_code', '==', shareCode)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error getting campaign by share code:', error);
    throw error;
  }
};

export const getUserCampaigns = async (userId: string) => {
  try {
    const q = query(
      collection(db, 'campaigns'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting campaigns:', error);
    throw error;
  }
};
